import { differenceInDays } from 'date-fns';

import type {
  ApiComponentType,
  ApiDocument,
  ApiDocumentCategory,
  ApiDocumentState,
  ApiDocumentUser,
  ApiNabanDocumentMetadata,
  ApiVlabelDocumentMetadata,
  ApiVlabelDocumentMetadataRaw,
} from '@izimi/api';
import { ApiDocumentSource } from '@izimi/api';
import {
  convertLocalDateToUTCDate,
  formatBytes,
  getExtension,
  getNameWithoutExtension,
} from '@izimi/utils';

import { _collection } from '../common';

const DEFAULT_TRASHING_DAYS = 180;
const CLOSE_TO_TRASHING_DAYS = 30;
const CLOSE_TO_DELETION_DAYS = 7;

export type Doc = {
  id: string;
  name: string;
  categories: ApiDocumentCategory[];
  labels: string[];
  isBeingScanned: boolean;
  isSeen: boolean;
  creationDate: string;
  modificationDate: string;
  scheduledTrashDate?: string;
  scheduledDeleteDate?: string;
  isOwner: boolean;
  size: number;
  trashingDate: string | undefined;
  sharedMessage: string | undefined;
  owner: ApiDocumentUser | undefined;
  sharedWith: ApiDocumentUser[];
  source: ApiDocumentSource;
  sources: ApiComponentType<'DocumentSource'>[];
  state: ApiDocumentState;
  isCopied?: boolean;
  hasVirus?: boolean;
  metadata: ApiDocument['metadata'];
  afterDeathSelection: ApiDocument['afterDeathSelection'];
};

class DocumentEntity implements Doc {
  readonly id: string;
  readonly name: string;
  readonly categories: ApiDocumentCategory[];
  readonly labels: string[];
  readonly isBeingScanned: boolean;
  readonly isSeen: boolean;
  readonly creationDate: string;
  readonly modificationDate: string;
  readonly scheduledTrashDate?: string;
  readonly scheduledDeleteDate?: string;
  readonly isOwner: boolean;
  readonly size: number;
  readonly trashingDate: string | undefined;
  readonly sharedMessage: string | undefined;
  readonly owner: ApiDocumentUser | undefined;
  readonly source: ApiDocumentSource;
  readonly sources: ApiComponentType<'DocumentSource'>[];
  readonly sharedWith: ApiDocumentUser[];
  readonly state: ApiDocumentState;
  readonly isCopied?: boolean;
  hasVirus?: boolean;
  readonly metadata: ApiDocument['metadata'];
  readonly afterDeathSelection: ApiDocument['afterDeathSelection'];

  readonly [_collection]?: string;

  constructor(document: Doc, collection?: string) {
    Object.assign(this, document);
    this[_collection] = collection;
  }

  private calculateDays(date?: string) {
    if (!date) return undefined;
    const now = convertLocalDateToUTCDate(new Date());
    const scheduledDate = convertLocalDateToUTCDate(new Date(date));
    const difference = differenceInDays(scheduledDate, now);
    return difference <= 0 ? 1 : difference;
  }

  get fileNameWithoutExtension() {
    return getNameWithoutExtension(this.name);
  }

  get isNabanDataType() {
    return this.source === ApiDocumentSource.Naban || this.sources[0]?.uploadSource === 'NABAN';
  }

  get isTrashed() {
    return this.state === 'TRASHED';
  }

  get shouldShowNabanInfoMessage() {
    return this.isOwner && this.isNabanDataType && this.source !== ApiDocumentSource.User;
  }

  /**
   * If the document was pushed by a notary, return the last source name.
   */
  get sourceName() {
    if (!this.isUploadedByNotary) {
      return null;
    }

    // Return the second-to-last source, as the last source of the owned document is the user himself.
    return this.sources[this.sources.length - 2]?.name ?? null;
  }

  get isPDF() {
    return this.extension === 'pdf';
  }

  get extension() {
    return getExtension(this.name);
  }

  get isSharedWithOthers() {
    return this.sharedWith ? this.sharedWith.length > 0 : false;
  }

  get isSharedByOtherUser() {
    return Boolean(!this.isOwner && this.owner !== undefined);
  }

  get isCertified() {
    return Boolean(this.metadata?.certification);
  }

  get isShareable(): boolean {
    return this.isOwner;
  }

  get mimeType() {
    if (this.extension) {
      // FIXME: what about other mime-types
      const mimeTypes: Record<string, string | undefined> = {
        pdf: 'application/pdf',
        png: 'image/png',
        svg: 'image/svg+xml',
        jpeg: 'image/jpeg',
        jpg: 'image/jpg',
      };
      return mimeTypes[this.extension];
    }
    return null;
  }

  get formattedSize() {
    return formatBytes(this.size, 2);
  }

  get isUploadedByNotary() {
    return this.source === ApiDocumentSource.Study;
  }

  get isUploadedByThirdParty() {
    return this.source === ApiDocumentSource.SSN;
  }

  get isUploadedByUser() {
    return this.source === ApiDocumentSource.User;
  }

  get isVlabel() {
    return this.source === ApiDocumentSource.Vlabel;
  }

  get isCopiedFromInheritedVault() {
    return Boolean(this.state === 'INHERITED' && this.isCopied);
  }

  get isActive() {
    return this.state === 'ACTIVE';
  }

  get isPending() {
    return this.state === 'PENDING';
  }

  get isInherited() {
    return this.state === 'INHERITED';
  }

  get daysTillTrashing() {
    return this.calculateDays(this.scheduledTrashDate);
  }

  get isCloseToTrashing() {
    return (this.daysTillTrashing ?? DEFAULT_TRASHING_DAYS) <= CLOSE_TO_TRASHING_DAYS;
  }

  get allowExtending() {
    const trashing = this.daysTillTrashing ?? DEFAULT_TRASHING_DAYS;
    const deletion = this.daysTillDeletion ?? DEFAULT_TRASHING_DAYS;
    return trashing < DEFAULT_TRASHING_DAYS || deletion < DEFAULT_TRASHING_DAYS;
  }

  get daysTillDeletion() {
    return this.calculateDays(this.scheduledDeleteDate);
  }

  get isCloseToDeletion() {
    return (this.daysTillDeletion ?? DEFAULT_TRASHING_DAYS) <= CLOSE_TO_DELETION_DAYS;
  }

  get nabanMetadata(): ApiNabanDocumentMetadata | undefined {
    if (!this.isNabanDataType || !this.metadata.providerData?.NABAN) {
      return undefined;
    }

    const nabanMetadata = this.metadata.providerData?.NABAN.reduce((acc, { key, value }) => {
      if (!key || !value) return acc;
      acc[key as keyof ApiNabanDocumentMetadata] = value;
      return acc;
    }, {} as ApiNabanDocumentMetadata);

    return nabanMetadata;
  }

  get vlabelMetaData(): ApiVlabelDocumentMetadata | undefined {
    if (!this.isVlabel || !this.metadata.providerData?.VLABEL) {
      return undefined;
    }

    const vlabelMetadata = this.metadata.providerData.VLABEL.reduce(
      (acc: ApiVlabelDocumentMetadataRaw, { key, value }) => {
        if (!key || !value) return acc;
        acc[key as keyof ApiVlabelDocumentMetadataRaw] = value;
        return acc;
      },
      {},
    );

    return {
      documentType: vlabelMetadata.DOCUMENTTYPE,
      taxFamily: vlabelMetadata.BELASTINGFAMILIE,
      ogmNumber: vlabelMetadata.OGMNUMMER,
      expeditionNumber: vlabelMetadata.EXPEDITIENUMMER,
      repertoryNumber: vlabelMetadata.REPERTORIUMNUMMER,
      heir: vlabelMetadata.ERFLATER,
    };
  }
}

export default DocumentEntity;
